import { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { Container, Row, Col, Card, Button, Form, Table, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import CustomNavbar from './NavBar'; // Import the new Navbar component

const UserManagement = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', firstName: '', lastName: '', phoneNumber: '', administrator: false });
  const [editUser, setEditUser] = useState<any>(null);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(res.data);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, [token, navigate]);

  const isAdmin = () => {
    const tokenData = JSON.parse(atob(token!.split('.')[1]));
    return tokenData['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Administrator';
  };

  const currentUserId = () => {
    const tokenData = JSON.parse(atob(token!.split('.')[1]));
    return parseInt(tokenData.sub);
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      alert('Username and password are required');
      return;
    }
    const payload = {
      Username: newUser.username,
      PasswordHash: newUser.password,
      FirstName: newUser.firstName,
      LastName: newUser.lastName,
      PhoneNumber: newUser.phoneNumber,
      Administrator: newUser.administrator
    };
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setUsers([...users, response.data]);
      setShowCreateModal(false);
      setNewUser({ username: '', password: '', firstName: '', lastName: '', phoneNumber: '', administrator: false });
    } catch (err) {
      const error = err as AxiosError;
      console.error('Failed to create user:', error);
      alert('Failed to create user: ' + (error.response?.data || 'Unknown error'));
    }
  };

  const handleUpdateUser = async (id: number, updatedUser: any) => {
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${id}`, updatedUser, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(users.map(u => (u.id === id ? res.data : u)));
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const handleEditUser = async () => {
    if (!editUser.username) {
      alert('Username is required');
      return;
    }
    const payload = {
      Username: editUser.username,
      FirstName: editUser.firstName,
      LastName: editUser.lastName,
      PhoneNumber: editUser.phoneNumber,
      Administrator: editUser.administrator
    };
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${editUser.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(users.map(u => (u.id === editUser.id ? response.data : u)));
      setShowEditModal(false);
      setEditUser(null);
    } catch (err) {
      const error = err as AxiosError;
      console.error('Failed to edit user:', error);
      alert('Failed to edit user: ' + (error.response?.data || 'Unknown error'));
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(users.filter(u => u.id !== id));
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  const handleResetPassword = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password/${resetUserId}`,
        JSON.stringify(newPassword),
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setShowResetModal(false);
      setNewPassword('');
      setResetUserId(null);
    } catch (err) {
      const error = err as AxiosError;
      console.error('Failed to reset password:', error);
      alert('Failed to reset password: ' + (error.response?.data || 'Unknown error'));
    }
  };

  if (!token) return <div>Please log in to continue</div>;

  const loggedInUserId = currentUserId();

  return (
    <>
      <CustomNavbar /> {/* Add the new Navbar component */}
      <Container fluid className="mt-5 mb-5">
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header as="h3" style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white', fontFamily: "'Poppins', sans-serif" }}>
                User Management
              </Card.Header>
              <Card.Body>
                {isAdmin() ? (
                  <>
                    <Button className="btn-custom-primary mb-3" onClick={() => setShowCreateModal(true)}>Create New User</Button>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Username</th>
                          <th>First Name</th>
                          <th>Last Name</th>
                          <th>Phone Number</th>
                          <th>Administrator</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(user => (
                          <tr key={user.id}>
                            <td>{user.id}</td>
                            <td>{user.username}</td>
                            <td>{user.firstName}</td>
                            <td>{user.lastName}</td>
                            <td>{user.phoneNumber}</td>
                            <td>{user.administrator ? 'Yes' : 'No'}</td>
                            <td>
                              <Button
                                className="btn-custom-primary me-2"
                                size="sm"
                                onClick={() => {
                                  setEditUser(user);
                                  setShowEditModal(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                className="btn-custom-primary me-2"
                                size="sm"
                                onClick={() => handleUpdateUser(user.id, { ...user, administrator: !user.administrator })}
                                disabled={user.id === loggedInUserId}
                              >
                                Toggle Admin
                              </Button>
                              <Button
                                className="btn-custom-danger me-2"
                                size="sm"
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={user.id === loggedInUserId}
                              >
                                Delete
                              </Button>
                              <Button
                                className="btn-custom-primary"
                                size="sm"
                                onClick={() => { setResetUserId(user.id); setShowResetModal(true); }}
                              >
                                Reset Password
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                ) : (
                  <div>
                    <h5>Reset Your Password</h5>
                    <Button
                      className="btn-custom-primary"
                      onClick={() => { setResetUserId(currentUserId()); setShowResetModal(true); }}
                    >
                      Reset Password
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Create User Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
            <Modal.Title>Create New User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Username</Form.Label>
                <Form.Control value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
              </Form.Group>
              <Form.Group>
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
              </Form.Group>
              <Form.Group>
                <Form.Label>First Name</Form.Label>
                <Form.Control value={newUser.firstName} onChange={e => setNewUser({ ...newUser, firstName: e.target.value })} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Last Name</Form.Label>
                <Form.Control value={newUser.lastName} onChange={e => setNewUser({ ...newUser, lastName: e.target.value })} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Phone Number</Form.Label>
                <Form.Control value={newUser.phoneNumber} onChange={e => setNewUser({ ...newUser, phoneNumber: e.target.value })} />
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Administrator"
                  checked={newUser.administrator}
                  onChange={e => setNewUser({ ...newUser, administrator: e.target.checked })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Close</Button>
            <Button className="btn-custom-primary" onClick={handleCreateUser}>Create</Button>
          </Modal.Footer>
        </Modal>

        {/* Edit User Modal */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
            <Modal.Title>Edit User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editUser && (
              <Form>
                <Form.Group>
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    value={editUser.username}
                    onChange={e => setEditUser({ ...editUser, username: e.target.value })}
                    required
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    value={editUser.firstName}
                    onChange={e => setEditUser({ ...editUser, firstName: e.target.value })}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    value={editUser.lastName}
                    onChange={e => setEditUser({ ...editUser, lastName: e.target.value })}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    value={editUser.phoneNumber}
                    onChange={e => setEditUser({ ...editUser, phoneNumber: e.target.value })}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="Administrator"
                    checked={editUser.administrator}
                    onChange={e => setEditUser({ ...editUser, administrator: e.target.checked })}
                    disabled={editUser.id === loggedInUserId} // Disable admin toggle for current user
                  />
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Close</Button>
            <Button className="btn-custom-primary" onClick={handleEditUser}>Save</Button>
          </Modal.Footer>
        </Modal>

        {/* Reset Password Modal */}
        <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(to right, #ff6b6b, #4ecdc4)', color: 'white' }}>
            <Modal.Title>Reset Password</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>New Password</Form.Label>
                <Form.Control type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResetModal(false)}>Close</Button>
            <Button className="btn-custom-primary" onClick={handleResetPassword}>Reset</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default UserManagement;